// src/pages/BillDetailPage.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useMediaQuery,
  Alert,
  Chip,
  Divider,
  Grid,
  Stack,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { isMobile } from 'react-device-detect';

const API_BASE = process.env.REACT_APP_API_URL;

const currency = (v) =>
  v ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "0.00";

const liters = (v) =>
  v ? Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }) : "0.000";

const fmt = (isoDate) => {
  if (!isoDate) return "N/A";
  const [y, m, d] = String(isoDate).split("-");
  return `${d}/${m}/${y}`;
};

export default function BillDetailPage() {
  const { username, fromDate, toDate } = useParams();
  const navigate = useNavigate();
  const billRef = useRef();
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const [billData, setBillData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchBillDetails = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await axios.post(`${API_BASE}/bill-pdf-json`, {
          username,
          bill_fromdate: fromDate,
          bill_todate: toDate
        });
        setBillData(response.data);
      } catch (e) {
        console.error('Error fetching bill details:', e);
        console.error('Error response:', e.response);
        setError("बिल तपशील लोड करताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.");
      } finally {
        setLoading(false);
      }
    };

    fetchBillDetails();
  }, [username, fromDate, toDate]);

  // Basic print function
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: '50vh' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>बिल लोड होत आहे...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          मागे जा
        </Button>
      </Box>
    );
  }

  if (!billData) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">बिल डेटा आढळला नाही.</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          मागे जा
        </Button>
      </Box>
    );
  }

  // Safe data extraction with null checks
  const { dairy = {}, bills = [], collections = [] } = billData;
  const bill = bills[0] || {};

  // If no bill data found, show message
  if (bills.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          या कालावधीसाठी कोणतेही बिल डेटा आढळले नाही.
        </Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          मागे जा
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: isSmallScreen ? 1 : 3, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header with Back Button and Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          मागे
        </Button>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          बिल तपशील
        </Typography>
        
        {/* Conditional PDF Download Button */}
        {!isMobile ? (
          <Button 
            variant="contained" 
            startIcon={<DownloadIcon />}
            onClick={handlePrint}
          >
            PDF डाउनलोड करा
          </Button>
        ) : (
          <Alert severity="info" sx={{ maxWidth: 300 }}>
            PDF डाउनलोड करण्यासाठी, कृपया डेस्कटॉप किंवा लॅपटॉप वापरा.
          </Alert>
        )}
      </Box>

      {/* Bill Content */}
      <Paper elevation={2} sx={{ p: isSmallScreen ? 2 : 4 }} ref={billRef}>
        {/* Company Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: isSmallScreen ? '1.1rem' : '1.5rem' }}>
            {dairy?.companyname || "डेअरी नाव"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {[dairy?.Place, dairy?.Tal, dairy?.Dist, dairy?.Statename].filter(Boolean).join(', ') || "डेअरीचा पत्ता"}
          </Typography>
          <Chip 
            label={`कालावधी: ${fmt(fromDate)} - ${fmt(toDate)}`} 
            sx={{ mt: 1, fontWeight: 'bold' }} 
            color="primary" 
            variant="outlined"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Farmer Information */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              उत्पादक माहिती
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2"><strong>नाव:</strong> {bill.farmer_name || 'N/A'} ({username})</Typography>
              <Typography variant="body2"><strong>मोबाइल:</strong> {bill.farmer_mobile || 'N/A'}</Typography>
              <Typography variant="body2"><strong>दुधाचा प्रकार:</strong> {bill.milktypecode === 1 ? 'गाय' : bill.milktypecode === 2 ? 'म्हैस' : 'मिश्र'}</Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              एकूण सारांश
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2"><strong>गायीचे दूध (लीटर):</strong> {liters(bill.cowqty)}</Typography>
              <Typography variant="body2"><strong>म्हशीचे दूध (लीटर):</strong> {liters(bill.buffqty)}</Typography>
              <Typography variant="body2"><strong>एकूण दूध (लीटर):</strong> {liters((bill.cowqty || 0) + (bill.buffqty || 0))}</Typography>
              <Typography variant="body2"><strong>एकूण रक्कम (₹):</strong> {currency((bill.cowamt || 0) + (bill.buffamt || 0))}</Typography>
            </Stack>
          </Grid>
        </Grid>

        {/* Collections Table - Only show if there are collections */}
        {collections && collections.length > 0 && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
              दुध संकलन तपशील
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isSmallScreen ? '12px' : '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>तारीख</th>
                    <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>शिफ्ट</th>
                    <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>दुध</th>
                    <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>प्रमाण (ली)</th>
                    <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>फॅट</th>
                    <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>दर (₹)</th>
                    <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>रक्कम (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map((collection, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>{fmt(collection.tran_date)}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>{collection.shift_code === 1 ? 'सकाळ' : 'संध्याकाळ'}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>{collection.milktypecode === 1 ? 'गाय' : 'म्हैस'}</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>{liters(collection.qty)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>{Number(collection.fat || 0).toFixed(1)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>{currency(collection.rate)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>{currency(collection.baseamt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </>
        )}

        {/* Financial Summary */}
        <Box sx={{ mt: 4, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            आर्थिक सारांश
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>गायीचे दूध रक्कम:</strong> ₹{currency(bill.cowamt)}</Typography>
              <Typography variant="body2"><strong>म्हशीचे दूध रक्कम:</strong> ₹{currency(bill.buffamt)}</Typography>
              <Typography variant="body2"><strong>एकूण रक्कम:</strong> ₹{currency((bill.cowamt || 0) + (bill.buffamt || 0))}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>गाय व्यवस्थापन शुल्क:</strong> ₹{currency(bill.cowcommissionamt)}</Typography>
              <Typography variant="body2"><strong>म्हैस व्यवस्थापन शुल्क:</strong> ₹{currency(bill.buffcommissionamt)}</Typography>
              <Typography variant="body2"><strong>ठेव कपात:</strong> ₹{currency(bill.deposit_kapat)}</Typography>
              <Typography variant="body2"><strong>मागील बाकी:</strong> ₹{currency(bill.previousdeposit)}</Typography>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              देय रक्कम: ₹{currency(bill.NetBill)}
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Typography variant="caption" display="block" sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
          हे बिल संगणकाद्वारे स्वयंचलितपणे तयार झाले आहे.
        </Typography>
      </Paper>

      {/* Mobile Guide Message */}
      {isMobile && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>सूचना:</strong> PDF डाउनलोड करण्यासाठी, कृपया ही पृष्ठ डेस्कटॉप किंवा लॅपटॉप कॉम्प्युटरवर उघडा.
        </Alert>
      )}
    </Box>
  );
}